import { type FunctionComponent, type ReactNode, useState } from 'react';
import { useParams } from 'react-router';

import { Checkbox, newAsyncDataEnvelope } from '@dr.pogodin/react-utils';

import Throbber from 'components/Throbber';
import { AdminPage, UserProfileHeader } from 'components/admin';
import { useUser } from 'hooks';
import { setBetaTester } from 'services/user/setBetaTester';

import {
  type RoleT,
  calcPermissions,
  getUserDisplayName,
  useGlobalState,
} from 'utils';

import './style.scss';

export const UserManagementPage: FunctionComponent = () => {
  const { userId } = useParams();
  if (!userId) throw Error('Internal error');

  const [busy, setBusy] = useState(false);
  const [, setList] = useGlobalState('users.list');

  const user = useUser(userId);

  const username = user.data ? getUserDisplayName(user.data) : '';

  const perms = calcPermissions(user.data?.roles);

  const isBetaTester = user.data?.roles.includes('BETA_TESTER') ?? false;

  const content: ReactNode = user.data ? (
    <>
      <UserProfileHeader user={user.data} />
      <div styleName="betaStatus">
        {perms.AI_APP_ACCESS && !isBetaTester
          ? (
            <div styleName="nonTesterBetaAccessMsg">
              This user has access to the AI application because of his role(s)
              other than the &laquo;Beta Tester&raquo; role.
            </div>
          ) : (
            <Checkbox
              checked={isBetaTester}
              label="Beta Tester"
              onChange={() => {
                void (async () => {
                  if (!user.data) throw Error('Internal error');
                  try {
                    setBusy(true);
                    const roles: RoleT[] = isBetaTester
                      ? user.data.roles.filter((item) => item !== 'BETA_TESTER')
                      : [...user.data.roles, 'BETA_TESTER'];
                    user.set({ ...user.data, roles });

                    const res = await setBetaTester(
                      user.data.id,
                      !isBetaTester,
                    );
                    user.set(res);
                    setList(newAsyncDataEnvelope());
                  } catch {
                    user.set(user.data);
                  } finally {
                    setBusy(false);
                  }
                })();
              }}
            />
          )}
        {busy ? <Throbber withDebounce withOverlay /> : null}
      </div>
    </>
  ) : <Throbber />;

  return (
    <AdminPage
      subTitle={`User / ${username}`}
      title="User management"
    >
      {content}
    </AdminPage>
  );
};
